import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertCircle,
  CheckCircle2,
  Code,
  Globe,
  Loader2,
  Send,
  XCircle,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from '@/hooks/use-toast';
import { PeppolAPI } from '@/services/peppol';
import {
  InvoicePeppolStatus,
  InvoicePeppolValidation,
  getPeppolStatusColor,
  getPeppolStatusLabel,
} from '@/types/peppol';

interface PeppolInvoiceActionsProps {
  invoiceId: number;
  invoiceNumber: string;
  clientName?: string;
  onUpdate?: () => void;
}

export function PeppolInvoiceActions({
  invoiceId,
  invoiceNumber,
  clientName,
  onUpdate,
}: PeppolInvoiceActionsProps) {
  const [status, setStatus] = useState<InvoicePeppolStatus | null>(null);
  const [validation, setValidation] = useState<InvoicePeppolValidation | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [validating, setValidating] = useState(false);
  const [xmlPreview, setXmlPreview] = useState<string | null>(null);
  const [loadingXml, setLoadingXml] = useState(false);
  const [isXmlDialogOpen, setIsXmlDialogOpen] = useState(false);
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);

  useEffect(() => {
    fetchPeppolStatus();
  }, [invoiceId]);

  const fetchPeppolStatus = async () => {
    setLoading(true);
    try {
      const peppolStatus = await PeppolAPI.getInvoicePeppolStatus(invoiceId);
      setStatus(peppolStatus);
    } catch (error) {
      console.error('Failed to fetch Peppol status:', error);
      // If endpoint doesn't exist or Peppol not configured, just don't show the component
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      const result = await PeppolAPI.validateInvoiceForPeppol(invoiceId);
      setValidation(result);
      if (result.valid) {
        toast({
          title: 'Validation Passed',
          description: 'Invoice is compliant with Peppol BIS Billing 3.0',
        });
      } else {
        toast({
          title: 'Validation Failed',
          description: `Found ${result.errors.length} error(s)`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Validation Error',
        description: 'Failed to validate invoice',
        variant: 'destructive',
      });
    } finally {
      setValidating(false);
    }
  };

  const handlePreviewXml = async () => {
    setLoadingXml(true);
    try {
      const result = await PeppolAPI.previewInvoicePeppolXml(invoiceId);
      setXmlPreview(result.xml);
      setIsXmlDialogOpen(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to generate UBL XML preview',
        variant: 'destructive',
      });
    } finally {
      setLoadingXml(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const result = await PeppolAPI.sendInvoiceViaPeppol(invoiceId, { validate_first: true });
      if (result.success) {
        toast({
          title: 'Invoice Sent',
          description: result.message || 'Invoice submitted to Peppol network',
        });
        setIsSendDialogOpen(false);
        fetchPeppolStatus();
        onUpdate?.();
      } else {
        toast({
          title: 'Send Failed',
          description: result.error || 'Failed to send invoice via Peppol',
          variant: 'destructive',
        });
        if (result.validation_errors?.length) {
          setValidation({
            valid: false,
            errors: result.validation_errors,
            warnings: [],
          });
        }
      }
    } catch (error: any) {
      toast({
        title: 'Send Failed',
        description: error.response?.data?.detail || 'Failed to send invoice via Peppol',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const handleCopyXml = () => {
    if (xmlPreview) {
      navigator.clipboard.writeText(xmlPreview);
      toast({
        title: 'Copied',
        description: 'UBL XML copied to clipboard',
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  // Don't show if Peppol is not enabled
  if (!status?.peppol_enabled) {
    return null;
  }

  const canSend = status.client_peppol_enabled && !status.has_been_sent;
  const hasBeenSent = status.has_been_sent;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            <CardTitle className="text-lg">Peppol E-Invoicing</CardTitle>
          </div>
          {hasBeenSent && status.status && (
            <Badge className={getPeppolStatusColor(status.status)}>
              {getPeppolStatusLabel(status.status)}
            </Badge>
          )}
        </div>
        <CardDescription>
          Send this invoice electronically via the Peppol network
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Client Peppol Status */}
        {!status.client_peppol_enabled && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Client Not Peppol-Enabled</AlertTitle>
            <AlertDescription>
              {clientName || 'This client'} does not have Peppol enabled. Please update the client settings to enable Peppol e-invoicing.
            </AlertDescription>
          </Alert>
        )}

        {/* Validation Results */}
        {validation && !validation.valid && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>Validation Errors</AlertTitle>
            <AlertDescription>
              <ul className="list-disc list-inside mt-2 space-y-1">
                {validation.errors.map((error, i) => (
                  <li key={i} className="text-sm">{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {validation?.warnings && validation.warnings.length > 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Warnings</AlertTitle>
            <AlertDescription>
              <ul className="list-disc list-inside mt-2 space-y-1">
                {validation.warnings.map((warning, i) => (
                  <li key={i} className="text-sm">{warning}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Sent Status Details */}
        {hasBeenSent && (
          <div className="space-y-2 text-sm">
            {status.sent_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sent at:</span>
                <span>{format(parseISO(status.sent_at), 'dd/MM/yyyy HH:mm')}</span>
              </div>
            )}
            {status.delivered_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Delivered at:</span>
                <span>{format(parseISO(status.delivered_at), 'dd/MM/yyyy HH:mm')}</span>
              </div>
            )}
            {status.response_code && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Response:</span>
                <span>{status.response_code} - {status.response_message}</span>
              </div>
            )}
          </div>
        )}

        <Separator />

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          {/* Validate Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleValidate}
            disabled={validating}
          >
            {validating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Validate
          </Button>

          {/* Preview XML Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviewXml}
            disabled={loadingXml}
          >
            {loadingXml ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Code className="h-4 w-4 mr-2" />
            )}
            Preview XML
          </Button>

          {/* Send Button */}
          {canSend && (
            <Dialog open={isSendDialogOpen} onOpenChange={setIsSendDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Send className="h-4 w-4 mr-2" />
                  Send via Peppol
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Send Invoice via Peppol</DialogTitle>
                  <DialogDescription>
                    This will send invoice {invoiceNumber} to {clientName || 'the client'} via the Peppol network.
                    The invoice will be validated before sending.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Alert>
                    <Globe className="h-4 w-4" />
                    <AlertTitle>Electronic Transmission</AlertTitle>
                    <AlertDescription>
                      The invoice will be converted to UBL format and transmitted through the Peppol network.
                      This is an official electronic invoice delivery method.
                    </AlertDescription>
                  </Alert>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsSendDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSend} disabled={sending}>
                    {sending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send Invoice
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Already Sent Badge */}
          {hasBeenSent && (
            <Badge variant="outline" className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Sent via Peppol
            </Badge>
          )}
        </div>

        {/* XML Preview Dialog */}
        <Dialog open={isXmlDialogOpen} onOpenChange={setIsXmlDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>UBL XML Preview</DialogTitle>
              <DialogDescription>
                This is the UBL XML that will be sent to the Peppol network
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-[500px] border rounded-md">
              <pre className="p-4 text-xs font-mono whitespace-pre-wrap">
                {xmlPreview}
              </pre>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={handleCopyXml}>
                Copy to Clipboard
              </Button>
              <Button onClick={() => setIsXmlDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
